import { AiPendingActionPlan, AiSessionContext } from '../services/api';

export type FollowUpSuggestion =
  | {
      kind: 'command';
      label: string;
      command: string;
      description: string;
    }
  | {
      kind: 'action';
      label: string;
      action: 'confirmPlan' | 'resetSession' | 'stats';
      description: string;
    };

export interface FollowUpSuggestionInput {
  session: AiSessionContext | null;
  visiblePlan: AiPendingActionPlan | null;
  subjectNames: string[];
}

const MAX_SUBJECTS = 2;

export const buildFollowUpSuggestions = ({ session, visiblePlan, subjectNames }: FollowUpSuggestionInput): FollowUpSuggestion[] => {
  const suggestions: FollowUpSuggestion[] = [];
  const seen = new Set<string>();
  const normalizedSubjects = subjectNames
    .map((name) => name.trim())
    .filter((name) => name.length > 0);

  const addCommand = (label: string, command: string, description: string) => {
    const key = `command:${command.toLowerCase()}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    suggestions.push({ kind: 'command', label, command, description });
  };

  const addAction = (label: string, action: 'confirmPlan' | 'resetSession' | 'stats', description: string) => {
    const key = `action:${action}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    suggestions.push({ kind: 'action', label, action, description });
  };

  if (visiblePlan) {
    addAction('Confirm plan', 'confirmPlan', 'Commit the pending write after revalidation.');
  }

  const primarySubject = normalizedSubjects[0] ?? session?.recentEntities[0]?.displayName ?? null;
  if (primarySubject) {
    addCommand(`Focus ${primarySubject}`, `focus ${primarySubject}`, 'Open this person on the canvas.');
    addCommand(`Ancestors of ${primarySubject}`, `ancestors of ${primarySubject}`, 'Inspect the upward family line.');
    addCommand(`Descendants of ${primarySubject}`, `descendants of ${primarySubject}`, 'Inspect the downward family line.');
  }

  normalizedSubjects.slice(0, MAX_SUBJECTS).forEach((subject) => {
    const surname = subject.split(' ').filter(Boolean).slice(-1)[0];
    if (surname) {
      addCommand(`Search ${surname}`, `search ${surname}`, 'Search the tree for matching people.');
    }
  });

  const recentCommand = session?.recentCommands?.find((command) => isLowRiskCommand(command));
  if (recentCommand) {
    addCommand('Repeat last command', recentCommand, 'Run the last read-only command again.');
  }

  addAction('Tree stats', 'stats', 'See a quick count of the current tree.');
  addAction('Reset session', 'resetSession', 'Clear bounded context and start fresh.');

  return suggestions.slice(0, 7);
};

const isLowRiskCommand = (command: string) => /^(find|resolve|search|focus|ancestors|descendants|path|stats|summary)\b/i.test(command.trim());
